import React, { useState, useEffect, useRef } from 'react';

function Chat({ apiUrl, token, onLogout }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const messagesEndRef = useRef(null);

  // Reconnaissance vocale
  const startListening = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('La reconnaissance vocale n\'est pas supportée par votre navigateur. Utilisez Chrome ou Safari.');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.lang = 'fr-FR';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(prev => prev + (prev ? ' ' : '') + transcript);
    };

    recognition.onerror = (event) => {
      console.error('Erreur reco vocale:', event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
  };

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadHistory = async () => {
    try {
      const res = await fetch(`${apiUrl}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.messages) setMessages(data.messages);
    } catch (err) {
      console.error(err);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userText = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userText }]);
    setLoading(true);

    try {
      const res = await fetch(`${apiUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ message: userText })
      });
      const data = await res.json();
      if (res.ok) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response,
          is_action: data.type === 'action'
        }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: '❌ Erreur' }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: '❌ Erreur réseau' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') sendMessage();
  };

  return (
    <div>
      <div className="chat-header">
        <h2>Assistant IA</h2>
        <button className="btn-logout" onClick={onLogout}>Déconnexion</button>
      </div>
      <div className="messages">
        {messages.length === 0 && <div className="loading">Commence la conversation !</div>}
        {messages.map((msg, i) => (
          <div key={i} className={`message ${msg.role === 'user' ? 'message-user' : 'message-bot'}`}>
            <div className={`bubble ${msg.role === 'user' ? 'bubble-user' : 'bubble-bot'}`}>
              {msg.content}
              {msg.is_action && <div className="action-badge">⚡ Action via n8n</div>}
            </div>
          </div>
        ))}
        {loading && <div className="loading">L'assistant réfléchit...</div>}
        <div ref={messagesEndRef} />
      </div>
      <div className="input-area">
        <button 
          className={`btn-mic ${isListening ? 'listening' : ''}`} 
          onClick={startListening}
          title="Parler"
        >
          🎤
        </button>
        <input 
          type="text" 
          placeholder="Écris ou parle..." 
          value={input} 
          onChange={(e) => setInput(e.target.value)} 
          onKeyDown={handleKeyDown} 
          disabled={loading}
        />
        <button className="btn-send" onClick={sendMessage} disabled={loading}>
          Envoyer
        </button>
      </div>
    </div>
  );
}

export default Chat;